// BYOK key + settings model (PRD 006). Keys live ONLY in this browser and go
// ONLY to the transcription provider — there is no first-party server. The AI
// participant is the user's own Claude Code over the local bridge (PRD 007), so
// no Anthropic key lives here anymore (PRD 003 §9); only the bridge pairing.

/** Transcription providers shipping in the MVP (001 §3). */
export type ProviderId = 'speechmatics' | 'soniox'

export type SpeechmaticsRegion = 'eu' | 'us'

/** Everything the settings screen owns, persisted as one record in IndexedDB. */
export interface StoredKeys {
  /** Chosen transcription provider (default Speechmatics). */
  provider: ProviderId
  /** The transcription provider's long-lived BYOK key. */
  transcriptionKey: string | null
  /** Local bridge origin for the AI participant (PRD 007). Empty = same origin
   *  (the gateway serves this SPA from its own loopback port), which is the
   *  normal case when the room is opened from `/brainstorm`. Set it only when
   *  running the web app on a different origin (e.g. the Vite dev server). */
  bridgeUrl: string
  /** Pairing token the gateway minted; binds this session to that bridge. */
  bridgeToken: string | null
  /** Name the room uses to address the AI (PRD 004 F2). Default "Claude". */
  aiTriggerName: string
  /** ElevenLabs API key for the AI's spoken voice (TTS). Browser-direct, BYOK —
   *  same trust model as the transcription key (team-internal, client-side is
   *  fine). Null = the AI stays text-only (no speak buttons, no auto-speak). */
  elevenLabsKey: string | null
  /** ElevenLabs voice id the AI speaks with. */
  ttsVoiceId: string
  /** Output device the AI voice plays to (HTMLAudioElement.setSinkId). Empty =
   *  the system default. Point it at a virtual cable (e.g. BlackHole) wired into
   *  a Meet/Zoom tab's mic so remote participants hear the AI too. */
  ttsOutputDeviceId: string
  /** Speechmatics realtime region. */
  speechmaticsRegion: SpeechmaticsRegion
  /**
   * Soniox direct-key path: send the user's own key on the socket instead of
   * minting a temp key. Off by default — unproven until the account is funded
   * (Phase-0 record). When on, tokens never expire (no refresh timer).
   */
  sonioxDirectKey: boolean
  /** Epoch-ms the first-run privacy disclosure (F4) was acknowledged. */
  disclosureAcceptedAt: number | null
}

/** Default ElevenLabs voice ("Rachel") — a calm, neutral preset that exists on
 *  every account, so a fresh key speaks without picking a voice first. */
export const DEFAULT_TTS_VOICE_ID = '21m00Tcm4TlvDq8ikWAM'

export const DEFAULT_KEYS: StoredKeys = {
  provider: 'speechmatics',
  transcriptionKey: null,
  bridgeUrl: '',
  bridgeToken: null,
  aiTriggerName: 'Claude',
  elevenLabsKey: null,
  ttsVoiceId: DEFAULT_TTS_VOICE_ID,
  ttsOutputDeviceId: '',
  speechmaticsRegion: 'eu',
  sonioxDirectKey: false,
  disclosureAcceptedAt: null
}

export const PROVIDER_LABELS: Record<ProviderId, string> = {
  speechmatics: 'Speechmatics',
  soniox: 'Soniox'
}
